import { useState, useEffect } from 'react';
import { getForecastFeatures, updateForecastFeatures, assembleForecastRun, trainForecastRun } from '../../api/analytics';

export default function FeatureImportancePanel({ configId, config }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [excluded, setExcluded] = useState(new Set(config?.config?.excluded_features ?? []));
  const [saving, setSaving] = useState(false);
  const [retraining, setRetraining] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getForecastFeatures(configId)
      .then(res => { setData(res); setLoading(false); })
      .catch(err => { setError(err.message || 'Failed to load features'); setLoading(false); });
  }, [configId]);

  function toggleExclude(feature) {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(feature)) next.delete(feature); else next.add(feature);
      return next;
    });
    setDirty(true);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await updateForecastFeatures(configId, {
        excluded_features: [...excluded],
        user_features: config?.config?.user_features || [],
      });
      setDirty(false);
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleRetrain() {
    setRetraining(true);
    setError(null);
    try {
      const assembleResult = await assembleForecastRun(configId);
      await trainForecastRun(configId, assembleResult.run_id);
      setDirty(false);
    } catch (err) {
      setError(err.message || 'Retrain failed');
    } finally {
      setRetraining(false);
    }
  }

  if (loading) return <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading features…</div>;
  if (error) return <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</div>;
  if (!data?.importance?.length) return <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>No feature importance data yet.</div>;

  const maxImp = data.importance[0]?.importance ?? 1;

  return (
    <div style={{ fontSize: '0.875rem' }}>
      {dirty && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#92400e' }}>Retrain Required — excluded features have changed.</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '0.8125rem' }}>
              {saving ? 'Saving…' : 'Save Exclusions'}
            </button>
            <button onClick={handleRetrain} disabled={retraining}
              style={{ padding: '4px 12px', borderRadius: '4px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: '0.8125rem' }}>
              {retraining ? 'Retraining…' : 'Retrain'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '4px 12px', alignItems: 'center' }}>
        <div style={{ fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '6px' }}>Feature</div>
        <div style={{ fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '6px' }}>Importance</div>
        <div style={{ fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '6px' }}>Exclude</div>
        {data.importance.map(item => (
          <>
            <div key={`n-${item.feature}`} style={{ color: excluded.has(item.feature) ? '#9ca3af' : '#111827', textDecoration: excluded.has(item.feature) ? 'line-through' : 'none' }}>
              {item.feature}
            </div>
            <div key={`b-${item.feature}`} style={{ minWidth: '120px' }}>
              <div style={{ height: '8px', borderRadius: '4px', background: '#e5e7eb', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(item.importance / maxImp) * 100}%`, background: excluded.has(item.feature) ? '#d1d5db' : '#2563eb', borderRadius: '4px' }} />
              </div>
            </div>
            <div key={`x-${item.feature}`} style={{ textAlign: 'center' }}>
              <input type="checkbox" checked={excluded.has(item.feature)}
                onChange={() => toggleExclude(item.feature)}
                style={{ accentColor: '#dc2626', width: '14px', height: '14px' }} />
            </div>
          </>
        ))}
      </div>

      {data.metrics && Object.keys(data.metrics).length > 0 && (
        <div style={{ marginTop: '16px', padding: '10px 12px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: '6px', fontSize: '0.8125rem' }}>Model Metrics</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {Object.entries(data.metrics).map(([k, v]) => (
              <div key={k}>
                <span style={{ color: '#6b7280' }}>{k}: </span>
                <span style={{ fontWeight: 600, color: '#111827' }}>{v.toFixed(4)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
